import { useState, useImperativeHandle, forwardRef, useCallback, useEffect, useRef } from "react";

const TextareaQuestionInput = forwardRef(
  (
    {
      activeNode,
      answers,
      language,
      i18nValue,
      onValueChange,
      colors = {},
      autoGrowSingleLine = false,
    },
    ref
  ) => {
    const {
      questionButtonBackgroundColor = "#D4A72C",
      questionBorderColor = "#E5E7EB",
      questionSoftBorderColor = "#F3F4F6",
      questionTextColor = "#111111",
      questionBackgroundColor = "#FFFFFF",
      isSimpleInput = false,
      simpleGroupedBorderColor = "#E5E7EB",
    } = colors;

    const currentAnswer = answers?.[activeNode.id] || "";
    const [localValue, setLocalValue] = useState(currentAnswer);
    const placeholder = i18nValue(activeNode, "placeholder", language, null, activeNode.placeholder || "");
    const textareaRef = useRef(null);

    const handleChange = useCallback((value) => {
      setLocalValue(value);
      if (onValueChange) {
        onValueChange(value);
      }
    }, [onValueChange]);

    const confirmAnswer = useCallback(() => {
      return localValue;
    }, [localValue]);

    const hasAnswer = useCallback(() => {
      return localValue && localValue.trim().length > 0;
    }, [localValue]);

    useImperativeHandle(ref, () => ({
      confirmAnswer,
      hasAnswer,
    }), [confirmAnswer, hasAnswer]);

    useEffect(() => {
      setLocalValue(currentAnswer);
    }, [currentAnswer]);

    useEffect(() => {
      if (!autoGrowSingleLine || !textareaRef.current) {
        return;
      }
      textareaRef.current.style.height = "0px";
      const nextHeight = Math.max(textareaRef.current.scrollHeight, 44);
      textareaRef.current.style.height = `${nextHeight}px`;
    }, [autoGrowSingleLine, localValue]);

    return (
      <div style={{ width: "100%" }}>
        <textarea
          ref={textareaRef}
          rows={1}
          value={localValue}
          onChange={(e) => handleChange(e.target.value)}
          placeholder={placeholder}
          style={{
            width: "100%",
            minHeight: autoGrowSingleLine ? 44 : 120,
            border: autoGrowSingleLine
              ? "none"
              : `1px solid ${isSimpleInput ? simpleGroupedBorderColor : questionBorderColor}`,
            borderBottom: autoGrowSingleLine
              ? `2px solid ${isSimpleInput ? simpleGroupedBorderColor : questionBorderColor}`
              : undefined,
            borderRadius: autoGrowSingleLine ? 0 : 12,
            padding: autoGrowSingleLine ? "0 0 10px" : 14,
            fontSize: autoGrowSingleLine ? 16 : 15,
            lineHeight: autoGrowSingleLine ? 1.45 : undefined,
            background: autoGrowSingleLine ? "transparent" : questionBackgroundColor,
            color: questionTextColor,
            outline: "none",
            boxShadow: "none",
            fontFamily: "inherit",
            resize: "none",
            overflow: "hidden",
          }}
        />
        {autoGrowSingleLine ? (
          <small
            style={{
              display: "block",
              marginTop: 8,
              color: questionTextColor,
              opacity: 0.86,
              fontSize: 12,
            }}
          >
            Enter to make a line break
          </small>
        ) : null}
      </div>
    );
  }
);

TextareaQuestionInput.displayName = "TextareaQuestionInput";

export default TextareaQuestionInput;
